import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@/shared/ui/Modal'
import { Button } from '@/shared/ui/Button'
import { restClient, getApiError } from '@/api/client'
import { endpoints } from '@/api/endpoints'

interface CityRef { id: number; name: string; packageCityId?: number }

interface Props {
  open: boolean
  onClose: () => void
  packageId: number
  cities: CityRef[]          // cities this edit applies to
  initial?: Record<string, any>  // prefill (single-city edit)
  onSuccess: () => void
}

// Mirrors the old dashboard's per-city edit form exactly: Min/Max Price,
// Discount Type/Value, and a single "Offer Valid Until" date. No commission
// or fee fields — those are backend-managed, never user-edited per city.
const empty = {
  min_price: '',
  max_price: '',
  discount_type: 'percentage',
  discount_value: '',
  offer_valid_until: '',
}

export function EditCityInfoModal({ open, onClose, packageId, cities, initial, onSuccess }: Props) {
  const { t } = useTranslation()
  const [form, setForm] = useState({ ...empty })
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const set = (k: keyof typeof empty, v: any) => setForm(f => ({ ...f, [k]: v }))

  // prefill on open
  useEffect(() => {
    if (!open) return
    setError('')
    setForm({
      min_price:         initial?.min_price ?? '',
      max_price:         initial?.max_price ?? '',
      discount_type:      initial?.discount_type ?? 'percentage',
      discount_value:     initial?.discount_value ?? '',
      offer_valid_until:  (initial?.offer_valid_until ?? '').slice(0, 10),
    })
  }, [open, initial])

  const handleSave = async () => {
    setError('')
    if (form.min_price !== '' && form.max_price !== '' && Number(form.min_price) >= Number(form.max_price)) {
      setError(t('packageDetails.cityForm.minMaxError'))
      return
    }
    setSaving(true)
    try {
      // Same endpoint + payload shape as the old dashboard's per-city price/
      // discount save (package-details-content.tsx `saveCityPricing` and
      // add-city-package.tsx): admin/package/create-packge-translation, as
      // multipart FormData with `cities_ids[i][...]` fields — NOT the
      // package-city-specific endpoint. FormData sidesteps Go's strict JSON
      // type binding (min_price/max_price/discount_value all arrive as
      // strings on the wire either way; Gin's form binder coerces each to
      // whatever type the struct field declares).
      const fd = new FormData()
      fd.append('package_id', String(packageId))
      cities.forEach((c, i) => {
        fd.append(`cities_ids[${i}][id]`, String(c.id))
        if (c.packageCityId) fd.append(`cities_ids[${i}][package_city_id]`, String(c.packageCityId))
        if (form.min_price !== '')         fd.append(`cities_ids[${i}][min_price]`, form.min_price)
        if (form.max_price !== '')         fd.append(`cities_ids[${i}][max_price]`, form.max_price)
        fd.append(`cities_ids[${i}][discount_type]`, form.discount_type)
        if (form.discount_value !== '')    fd.append(`cities_ids[${i}][discount_value]`, form.discount_value)
        if (form.offer_valid_until !== '') fd.append(`cities_ids[${i}][offer_valid_until]`, form.offer_valid_until)
      })

      await restClient.post(endpoints.package.createUpdate, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      onSuccess()
      onClose()
    } catch (e) {
      setError(getApiError(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={t('packageDetails.editMultipleCity')} size="md">
      <div className="space-y-4">
        {/* city chips */}
        <div className="flex flex-wrap gap-2">
          {cities.map(c => (
            <span key={c.id} className="px-3 py-1 rounded-md bg-brand-500 text-white text-xs font-semibold">{c.name}</span>
          ))}
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-600">{error}</div>}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t('packageDetails.cityForm.minPrice')}><Money value={form.min_price} onChange={v => set('min_price', v)} /></Field>
          <Field label={t('packageDetails.cityForm.maxPrice')}><Money value={form.max_price} onChange={v => set('max_price', v)} /></Field>
          <Field label={t('packageDetails.tiles.discountType')}>
            <select value={form.discount_type} onChange={e => set('discount_type', e.target.value)} className={inputCls}>
              <option value="percentage">{t('packageDetails.percentage')}</option>
              <option value="amount">{t('packageDetails.flatAmount')}</option>
            </select>
          </Field>
          <Field label={t('packageDetails.tiles.discountValue')}>
            <input type="number" value={form.discount_value} onChange={e => set('discount_value', e.target.value)} placeholder="0.00" className={inputCls} />
          </Field>
          <Field label={t('packageDetails.cityForm.offerValidUntil')}>
            <input type="date" value={form.offer_valid_until} onChange={e => set('offer_valid_until', e.target.value)} className={inputCls} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3 pt-2">
          <Button variant="outline" className="justify-center" onClick={onClose}>{t('packageDetails.cancel')}</Button>
          <Button className="justify-center" loading={saving} onClick={handleSave}>{t('packageDetails.save')}</Button>
        </div>
      </div>
    </Modal>
  )
}

const inputCls = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-400'

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-bold text-gray-700 block mb-1.5">{label}</label>
      {children}
    </div>
  )
}

function Money({ value, onChange }: { value: any; onChange: (v: string) => void }) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-semibold">AED</span>
      <input type="number" value={value} onChange={e => onChange(e.target.value)} placeholder="0.00"
        className={inputCls + ' pl-11 text-right'} />
    </div>
  )
}
